import { Entity, System } from "ecsy";
import { AoE, Collision, Physics, Targeting, TargetingSpec, Transform, Healer, Health, RangeType, Buff, Attacker, DamageType } from "../components";
import { applyImpulse } from "../box2dUtils";
import { PhysicsConstants } from "../constants";
import { destroyEntity } from "../entityFactory";
import { vec2 } from "@tlaukkan/tsm";
import { addComponent, getOrAddComponent, metersToPixels, uniformVec2 } from "../utils";
import { Game } from "../game";
import { getEntityPlayer } from "../composition";

export class AreaEffectSystem extends System {

	game: Game;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute(delta: number, time: number) {
		this.queries.aoe.added?.forEach(entity => {
			const aoe = entity.getComponent(AoE);
			const transform = entity.getMutableComponent(Transform);
			transform.size = uniformVec2(10 * 2);
			const targeting = getOrAddComponent(entity, Targeting);
			aoe.targetingIndex = targeting.addSpec(RangeType.Circle, aoe.currentRadius, aoe.targetFilter)
		});

		this.queries.aoe.results.forEach(entity => {
			const aoe = entity.getMutableComponent(AoE);
			const transform = entity.getMutableComponent(Transform);
			const targeting = entity.getComponent(Targeting);
			const center = transform.position;
			const targetingSpec = targeting.specs[aoe.targetingIndex];
			let hits = targeting.getTargets(aoe.targetingIndex);
			const healer = entity.getComponent(Healer);
			hits = hits.filter(hit =>
				!aoe.alreadyHit.has(hit.id) &&
				!aoe.entityFilter.has(hit.id)
			);

			hits.forEach(hit => {
				if (process.env.is_server) {
					if (!hit.hasComponent(Collision)) {
						addComponent(hit, Collision, { collidedWith: [] });
					}
					hit.getComponent(Collision).collidedWith.push({ entity: entity, canDamage: true });
				}
				const physics = hit.getComponent(Physics);
				const transform = hit.getComponent(Transform);
				if (physics && transform) {
					const direction = vec2.difference(transform.position.copy(), center).normalize();
					const push = direction.scale(aoe.force);
					applyImpulse(hit, push);
				}

				const health = hit.getMutableComponent(Health); //mutable because this triggers showing the health, TODO, hacky
				if (healer && health) {
					addComponent(hit, Buff, { buffs: [{ stat: "health", value: healer.healing, max: 1 }] });
				}

				if (aoe.hitOnce) {
					aoe.alreadyHit.add(hit.id);
				}
			});

			aoe.durationRemaining -= PhysicsConstants.StepRate;
			if (aoe.durationRemaining < 0) {
				this.checkAndReportExplosionKills(entity);
				destroyEntity(entity);
			}
			else {
				const change = PhysicsConstants.StepRate * aoe.radiusPerSecond
				aoe.currentRadius += change;
				if (change > 0) {
					entity.getMutableComponent(Targeting); //mark changed
					targetingSpec.range = aoe.currentRadius;
				}
			}

			//TODO: De-hack this. This hack makes us produce the right scale after the renderer does its thing.
			transform.scale = (metersToPixels(aoe.currentRadius) / metersToPixels(10));
		});
	}

	checkAndReportExplosionKills(entity: Entity) {
		const attacker = entity.getComponent(Attacker);
		if (!attacker || attacker.damageType !== DamageType.Explosion || attacker.kills === 0) return;
		const player = getEntityPlayer(entity, this.game);
		if (!player) return;
		const maxKills = this.game.getRoundStat(player.id, 'maxSingleExplosionKills');
		if (attacker.kills > maxKills) {
			this.game.setRoundStat(player.id, 'maxSingleExplosionKills', attacker.kills);
		}
	}
}

AreaEffectSystem.queries = {
	aoe: { components: [AoE, Transform], listen: { added: true } },
};
